import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { loadExerciseLibrary, type ExerciseLibraryData } from '../features/exercises/api';
import { mediaUrl } from '../lib/storage';
import type { EquipmentRow } from '../lib/types';
import { EQUIPMENT_CATEGORIES, EXERCISE_KINDS, MUSCLE_GROUPS, SPORTS, label } from '../lib/vocab';
import { Chip, ChipRow } from '../components/Chip';
import { Segmented } from '../components/Segmented';
import { Button } from '../components/Button';
import { Spinner } from '../components/Spinner';
import { searchRank, type Searchable } from '../lib/search';
import {
  EMPTY_FILTERS,
  filtersFromParams,
  filtersToParams,
  isDefault,
  matches,
  sortRows,
  type FilterAxis,
  type Filters,
  type Row,
  type SortKey,
  type TriState,
} from '../features/exercises/filters';

const TRI_OPTIONS: ReadonlyArray<{ value: TriState; label: string }> = [
  { value: 'any', label: 'Any' },
  { value: 'with', label: 'With' },
  { value: 'without', label: 'Without' },
];

const SORT_OPTIONS: ReadonlyArray<{ value: SortKey; label: string }> = [
  { value: 'name', label: 'Name' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'missing_media', label: 'Missing media first' },
];

export function ExercisesListPage() {
  const [data, setData] = useState<ExerciseLibraryData | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const [equipQuery, setEquipQuery] = useState('');

  useEffect(() => {
    loadExerciseLibrary().then(setData).catch(() => setData({ exercises: [], media: [], equipmentLinks: [], equipment: [] }));
  }, []);

  function updateParams(mutate: (p: URLSearchParams) => void) {
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        mutate(out);
        return out;
      },
      { replace: true },
    );
  }

  function setQuery(next: string) {
    updateParams((p) => (next ? p.set('q', next) : p.delete('q')));
  }

  function setFilters(next: Filters) {
    updateParams((p) => filtersToParams(p, next));
  }

  function patchFilters(patch: Partial<Filters>) {
    setFilters({ ...filters, ...patch });
  }

  function toggleSetValue<T>(set: ReadonlySet<T>, value: T): Set<T> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  }

  function clearAll() {
    setQuery('');
    setFilters(EMPTY_FILTERS);
  }

  // --- derive rows -----------------------------------------------------

  const equipmentById = useMemo(() => new Map((data?.equipment ?? []).map((e) => [e.id, e])), [data]);

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const mediaByExercise = new Map<string, typeof data.media>();
    for (const m of data.media) {
      const list = mediaByExercise.get(m.exercise_id) ?? [];
      list.push(m);
      mediaByExercise.set(m.exercise_id, list);
    }
    const equipmentByExercise = new Map<string, string[]>();
    for (const link of data.equipmentLinks) {
      const list = equipmentByExercise.get(link.exercise_id) ?? [];
      list.push(link.equipment_id);
      equipmentByExercise.set(link.exercise_id, list);
    }
    return data.exercises.map((e) => {
      const media = mediaByExercise.get(e.id) ?? [];
      const equipmentIds = equipmentByExercise.get(e.id) ?? [];
      const hasSteps = (arr: string[] | null | undefined) => Array.isArray(arr) && arr.length > 0;
      return {
        id: e.id,
        name_en: e.name_en,
        name_he: e.name_he,
        sport: e.sport,
        muscle_group: e.muscle_group,
        kind: e.kind,
        archived: Boolean(e.archived_at),
        hasPhoto: Boolean(media.find((m) => m.kind === 'demo_photo')),
        videoCount: media.filter((m) => m.kind === 'video' || m.kind === 'demo_video').length,
        imageCount: media.filter((m) => m.kind === 'image').length,
        hasHowTo: hasSteps(e.description_steps_en) || hasSteps(e.description_steps_he),
        equipmentIds,
        updatedAt: e.updated_at,
      };
    });
  }, [data]);

  /** Row id -> extra search fields (muscle label, sport label, kind label, equipment names). Built once per data load. */
  const searchExtraById = useMemo(() => {
    const out = new Map<string, string[]>();
    for (const r of rows) {
      const equipNames = r.equipmentIds.map((id) => equipmentById.get(id)).filter(Boolean) as { name_en: string; name_he: string }[];
      out.set(r.id, [
        label(r.muscle_group),
        label(r.sport),
        label(r.kind),
        ...equipNames.flatMap((eq) => [eq.name_en, eq.name_he]),
      ]);
    }
    return out;
  }, [rows, equipmentById]);

  const searchMatchedIds = useMemo(() => {
    if (!q.trim()) return null;
    const searchable: Searchable<string>[] = rows.map((r) => ({
      item: r.id,
      names: [r.name_en, r.name_he],
      extra: searchExtraById.get(r.id) ?? [],
    }));
    return new Set(searchRank(q, searchable).map((x) => x.item));
  }, [rows, q, searchExtraById]);

  function passesSearch(id: string): boolean {
    return searchMatchedIds === null || searchMatchedIds.has(id);
  }

  // --- faceted counts ----------------------------------------------------

  function countsBy(axis: FilterAxis, keyFn: (r: Row) => string[]): Map<string, number> {
    const exclude = new Set<FilterAxis>([axis]);
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (!matches(r, filters, exclude)) continue;
      if (!passesSearch(r.id)) continue;
      for (const k of keyFn(r)) counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return counts;
  }

  function triCounts(axis: FilterAxis, hasFn: (r: Row) => boolean): { withCount: number; withoutCount: number } {
    const exclude = new Set<FilterAxis>([axis]);
    let withCount = 0;
    let withoutCount = 0;
    for (const r of rows) {
      if (!matches(r, filters, exclude)) continue;
      if (!passesSearch(r.id)) continue;
      if (hasFn(r)) withCount++;
      else withoutCount++;
    }
    return { withCount, withoutCount };
  }

  const sportCounts = useMemo(() => countsBy('sport', (r) => [r.sport]), [rows, filters]);
  const muscleCounts = useMemo(() => countsBy('muscles', (r) => [r.muscle_group]), [rows, filters]);
  const kindCounts = useMemo(() => countsBy('kind', (r) => [r.kind]), [rows, filters]);
  const equipmentCounts = useMemo(() => countsBy('equipmentIds', (r) => r.equipmentIds), [rows, filters]);
  const photoCounts = useMemo(() => triCounts('photo', (r) => r.hasPhoto), [rows, filters]);
  const videoCounts = useMemo(() => triCounts('video', (r) => r.videoCount > 0), [rows, filters]);
  const imageCounts = useMemo(() => triCounts('images', (r) => r.imageCount > 0), [rows, filters]);
  const howtoCounts = useMemo(() => triCounts('howto', (r) => r.hasHowTo), [rows, filters]);

  // --- coverage summary (library-wide, respects "show archived") --------

  const coverage = useMemo(() => {
    const scope = rows.filter((r) => filters.showArchived || !r.archived);
    const photos = scope.filter((r) => r.hasPhoto).length;
    const videos = scope.filter((r) => r.videoCount > 0).length;
    return { total: scope.length, photos, videos };
  }, [rows, filters.showArchived]);

  // --- final list ----------------------------------------------------

  const finalRows = useMemo(() => {
    const facetFiltered = rows.filter((r) => matches(r, filters, new Set()));
    if (q.trim()) {
      const searchable: Searchable<Row>[] = facetFiltered.map((r) => ({
        item: r,
        names: [r.name_en, r.name_he],
        extra: searchExtraById.get(r.id) ?? [],
      }));
      return searchRank(q, searchable).map((x) => x.item);
    }
    return sortRows(facetFiltered, filters.sort);
  }, [rows, filters, q, searchExtraById]);

  // --- equipment picker (categorized, searchable) -----------------------

  const equipmentByCategory = useMemo(() => {
    const byCat = new Map<string, EquipmentRow[]>();
    const list = (data?.equipment ?? []).filter((e) => !e.archived_at);
    const filtered = equipQuery.trim()
      ? searchRank(
          equipQuery,
          list.map((e) => ({ item: e, names: [e.name_en, e.name_he], extra: [label(e.category)] })),
        ).map((x) => x.item)
      : list;
    for (const eq of filtered) {
      const arr = byCat.get(eq.category) ?? [];
      arr.push(eq);
      byCat.set(eq.category, arr);
    }
    return byCat;
  }, [data, equipQuery]);

  // --- active-filter chips -----------------------------------------------

  type ActiveChip = { key: string; text: string; onRemove: () => void };
  const activeChips: ActiveChip[] = [];
  if (filters.sport) activeChips.push({ key: 'sport', text: `Sport: ${label(filters.sport)}`, onRemove: () => patchFilters({ sport: null }) });
  for (const m of filters.muscles) {
    activeChips.push({ key: `m-${m}`, text: `Muscle: ${label(m)}`, onRemove: () => patchFilters({ muscles: toggleSetValue(filters.muscles, m) }) });
  }
  if (filters.kind) activeChips.push({ key: 'kind', text: `Kind: ${label(filters.kind)}`, onRemove: () => patchFilters({ kind: null }) });
  for (const id of filters.equipmentIds) {
    const eq = equipmentById.get(id);
    activeChips.push({
      key: `eq-${id}`,
      text: `Equipment: ${eq?.name_en ?? id}`,
      onRemove: () => patchFilters({ equipmentIds: toggleSetValue(filters.equipmentIds, id) }),
    });
  }
  if (filters.photo !== 'any') activeChips.push({ key: 'photo', text: `Photo: ${filters.photo}`, onRemove: () => patchFilters({ photo: 'any' }) });
  if (filters.video !== 'any') activeChips.push({ key: 'video', text: `Video: ${filters.video}`, onRemove: () => patchFilters({ video: 'any' }) });
  if (filters.images !== 'any') activeChips.push({ key: 'images', text: `Images: ${filters.images}`, onRemove: () => patchFilters({ images: 'any' }) });
  if (filters.howto !== 'any') activeChips.push({ key: 'howto', text: `How-to: ${filters.howto}`, onRemove: () => patchFilters({ howto: 'any' }) });
  if (filters.showArchived) activeChips.push({ key: 'archived', text: 'Including archived', onRemove: () => patchFilters({ showArchived: false }) });
  if (q.trim()) activeChips.push({ key: 'q', text: `Search: "${q.trim()}"`, onRemove: () => setQuery('') });

  const hasAnyFilter = !isDefault(filters) || Boolean(q.trim());

  return (
    <div>
      <div className="topbar">
        <h1 className="page-title">Exercises</h1>
        <Link to="/exercises/new">
          <Button>New exercise</Button>
        </Link>
      </div>

      {data ? (
        <div className="coverage-bar">
          <button
            type="button"
            className={`coverage-stat ${coverage.photos === coverage.total ? 'all-covered' : 'has-gaps'}`}
            onClick={() => patchFilters({ photo: 'without' })}
          >
            Photos <strong>{coverage.photos}/{coverage.total}</strong>
          </button>
          <button
            type="button"
            className={`coverage-stat ${coverage.videos === coverage.total ? 'all-covered' : 'has-gaps'}`}
            onClick={() => patchFilters({ video: 'without' })}
          >
            Videos <strong>{coverage.videos}/{coverage.total}</strong>
          </button>
        </div>
      ) : null}

      <div className="filter-panel">
        <input
          className="input"
          placeholder="Search name, muscle, equipment (en/he)…"
          value={q}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: '100%' }}
        />

        <div className="filter-row">
          <div className="filter-group">
            <span className="filter-group-label">Sport</span>
            <ChipRow>
              <Chip active={filters.sport === null} onClick={() => patchFilters({ sport: null })}>
                All
              </Chip>
              {SPORTS.map((s) => (
                <Chip key={s} active={filters.sport === s} onClick={() => patchFilters({ sport: filters.sport === s ? null : s })}>
                  {label(s)} ({sportCounts.get(s) ?? 0})
                </Chip>
              ))}
            </ChipRow>
          </div>

          <div className="filter-group">
            <span className="filter-group-label">Kind</span>
            <ChipRow>
              <Chip active={filters.kind === null} onClick={() => patchFilters({ kind: null })}>
                All
              </Chip>
              {EXERCISE_KINDS.map((k) => (
                <Chip key={k} active={filters.kind === k} onClick={() => patchFilters({ kind: filters.kind === k ? null : k })}>
                  {label(k)} ({kindCounts.get(k) ?? 0})
                </Chip>
              ))}
            </ChipRow>
          </div>
        </div>

        <div className="filter-group">
          <span className="filter-group-label">Muscle group (any of)</span>
          <ChipRow>
            {MUSCLE_GROUPS.map((m) => (
              <Chip key={m} active={filters.muscles.has(m)} onClick={() => patchFilters({ muscles: toggleSetValue(filters.muscles, m) })}>
                {label(m)} ({muscleCounts.get(m) ?? 0})
              </Chip>
            ))}
          </ChipRow>
        </div>

        <details className="filter-group">
          <summary className="filter-group-label" style={{ cursor: 'pointer' }}>
            Equipment{filters.equipmentIds.size ? ` (${filters.equipmentIds.size} selected)` : ''}
          </summary>
          <div className="equipment-picker-popover">
            <input
              className="input"
              placeholder="Search equipment…"
              value={equipQuery}
              onChange={(e) => setEquipQuery(e.target.value)}
            />
            {EQUIPMENT_CATEGORIES.filter((c) => (equipmentByCategory.get(c) ?? []).length > 0).map((cat) => (
              <div key={cat} className="equipment-picker-category">
                <span className="equipment-picker-category-label">{label(cat)}</span>
                <ChipRow>
                  {(equipmentByCategory.get(cat) ?? []).map((eq) => (
                    <Chip
                      key={eq.id}
                      active={filters.equipmentIds.has(eq.id)}
                      onClick={() => patchFilters({ equipmentIds: toggleSetValue(filters.equipmentIds, eq.id) })}
                    >
                      {eq.name_en} ({equipmentCounts.get(eq.id) ?? 0})
                    </Chip>
                  ))}
                </ChipRow>
              </div>
            ))}
          </div>
        </details>

        <div className="filter-row">
          <Segmented
            label="Photo"
            value={filters.photo}
            onChange={(v) => patchFilters({ photo: v })}
            options={TRI_OPTIONS.map((o) => ({
              ...o,
              label: o.value === 'any' ? o.label : `${o.label} (${o.value === 'with' ? photoCounts.withCount : photoCounts.withoutCount})`,
            }))}
          />
          <Segmented
            label="Video"
            value={filters.video}
            onChange={(v) => patchFilters({ video: v })}
            options={TRI_OPTIONS.map((o) => ({
              ...o,
              label: o.value === 'any' ? o.label : `${o.label} (${o.value === 'with' ? videoCounts.withCount : videoCounts.withoutCount})`,
            }))}
          />
          <Segmented
            label="Extra images"
            value={filters.images}
            onChange={(v) => patchFilters({ images: v })}
            options={TRI_OPTIONS.map((o) => ({
              ...o,
              label: o.value === 'any' ? o.label : `${o.label} (${o.value === 'with' ? imageCounts.withCount : imageCounts.withoutCount})`,
            }))}
          />
          <Segmented
            label="How-to"
            value={filters.howto}
            onChange={(v) => patchFilters({ howto: v })}
            options={TRI_OPTIONS.map((o) => ({
              ...o,
              label: o.value === 'any' ? o.label : `${o.label} (${o.value === 'with' ? howtoCounts.withCount : howtoCounts.withoutCount})`,
            }))}
          />
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <span className="filter-group-label">Sort</span>
            <select className="select" value={filters.sort} onChange={(e) => patchFilters({ sort: e.target.value as SortKey })}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <span className="filter-group-label">Archived</span>
            <ChipRow>
              <Chip active={filters.showArchived} onClick={() => patchFilters({ showArchived: !filters.showArchived })}>
                {filters.showArchived ? 'Showing archived' : 'Show archived'}
              </Chip>
            </ChipRow>
          </div>
        </div>
      </div>

      <div className="results-bar">
        <span className="results-count">
          {data === null ? '…' : `${finalRows.length} of ${rows.length} exercises`}
        </span>
        {hasAnyFilter ? (
          <Button tone="neutral" size="sm" onClick={clearAll}>
            Clear all
          </Button>
        ) : null}
      </div>

      {activeChips.length > 0 ? (
        <div className="active-filters" style={{ marginBottom: 16 }}>
          {activeChips.map((c) => (
            <span key={c.key} className="active-chip">
              {c.text}
              <button type="button" onClick={c.onRemove} aria-label={`Remove ${c.text}`}>
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {data === null ? (
        <Spinner />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th />
                <th>Name</th>
                <th>Sport</th>
                <th>Muscle</th>
                <th>Kind</th>
                <th>Photo</th>
                <th>Video</th>
                <th>Images</th>
                <th>How-to</th>
                <th>Equipment</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {finalRows.map((r) => {
                const thumb = mediaUrl((data.media.find((m) => m.exercise_id === r.id && m.kind === 'demo_photo') ?? null)?.path ?? null);
                return (
                  <tr key={r.id}>
                    <td>{thumb ? <img className="thumb" src={thumb} alt="" /> : <div className="thumb" />}</td>
                    <td>
                      <Link to={`/exercises/${r.id}`} style={{ color: 'inherit', fontWeight: 600, textDecoration: 'none' }}>
                        {r.name_en}
                      </Link>
                      <div className="muted" dir="auto">
                        {r.name_he}
                      </div>
                    </td>
                    <td>{label(r.sport)}</td>
                    <td>{label(r.muscle_group)}</td>
                    <td>{label(r.kind)}</td>
                    <td>
                      <span className={`badge ${r.hasPhoto ? 'ok' : 'warn'}`}>{r.hasPhoto ? 'ok' : 'missing'}</span>
                    </td>
                    <td>
                      <span className={`badge ${r.videoCount > 0 ? 'ok' : 'warn'}`}>{r.videoCount}</span>
                    </td>
                    <td>
                      <span className="badge">{r.imageCount}</span>
                    </td>
                    <td>
                      <span className={`badge ${r.hasHowTo ? 'ok' : 'warn'}`}>{r.hasHowTo ? 'yes' : 'no'}</span>
                    </td>
                    <td>
                      <span className="badge">{r.equipmentIds.length}</span>
                    </td>
                    <td>{r.archived ? <span className="badge archived">archived</span> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data !== null && finalRows.length === 0 ? <p className="muted">No exercises match.</p> : null}
    </div>
  );
}
